
import {
  defineComponent,
  PropType,
  ref,
  watch,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import EmojisButton from '@/components/fields/partials/EmojisButton.vue';
import FileManager from '@/components/file-manager/FileManager.vue';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';

interface SlotSymbol {
  id: string;
  type: 'image' | 'text';
  image: string;
  text: string;
  label: string;
  color: string;
  backgroundColor: string;
  fontSize: number | null; // null = auto
}

export default defineComponent({
  name: 'GameSlotSymbolsEditor',

  components: {
    InputText,
    InputNumber,
    Dropdown,
    Button,
    EmojisButton,
    FileManager,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);
    const symbols = ref<SlotSymbol[]>([]);

    const showFileManager = ref(false);
    const imagePickerIndex = ref<number>(-1);

    const typeOptions = [
      { label: t('templateBuilder.games.text'), value: 'text' },
      { label: 'Image', value: 'image' },
    ];

    const defaultEmojis = ['🍒', '🍋', '🍊', '🍇', '⭐', '💎', '7️⃣', '🔔'];

    const detectType = (item: any): 'image' | 'text' => {
      if (item.type === 'image') return 'image';
      const img = item.image || '';
      if (img.match(/^(https?:\/\/|\/)/)) return 'image';
      return 'text';
    };

    const parseInitialValue = () => {
      try {
        const value = property.value.value;
        if (value && typeof value === 'string') {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            symbols.value = parsed.map((item: any, idx: number) => {
              const type = detectType(item);
              return {
                id: item.id || `symbol_${idx}`,
                type,
                image: type === 'image' ? (item.image || '') : '',
                text: type === 'text' ? (item.text || item.image || '') : '',
                label: item.label || '',
                color: item.color || '#333333',
                backgroundColor: item.backgroundColor || '',
                fontSize: item.fontSize != null ? Number(item.fontSize) : null,
              };
            });
            return;
          }
        }
      } catch (e) {
        // Ignore
      }

      // Default symbols
      symbols.value = [
        { id: 'cherry', type: 'text', image: '', text: '🍒', label: 'Cerise', color: '#333333', backgroundColor: '', fontSize: null },
        { id: 'lemon', type: 'text', image: '', text: '🍋', label: 'Citron', color: '#333333', backgroundColor: '', fontSize: null },
        { id: 'orange', type: 'text', image: '', text: '🍊', label: 'Orange', color: '#333333', backgroundColor: '', fontSize: null },
        { id: 'seven', type: 'text', image: '', text: '7️⃣', label: 'Sept', color: '#333333', backgroundColor: '', fontSize: null },
      ];
    };

    const emitChange = () => {
      // Build output: put display value in 'image' for backward compat
      const output = symbols.value.map((s, idx) => {
        const base: Record<string, any> = {
          id: s.id || `symbol_${idx}`,
          type: s.type,
          label: s.type === 'text' ? (s.text || '') : s.id,
          image: s.type === 'image' ? s.image : (s.text || ''),
        };
        if (s.type === 'text') {
          base.text = s.text || '';
          base.color = s.color || '#333333';
          if (s.fontSize != null) {
            base.fontSize = s.fontSize;
          }
        }
        if (s.backgroundColor) {
          base.backgroundColor = s.backgroundColor;
        }
        return base;
      });
      const jsonValue = JSON.stringify(output);
      property.value.value = jsonValue;
      emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
    };

    const onSymbolChange = () => {
      emitChange();
    };

    const onEmojiInsert = (index: number, emoji: string) => {
      symbols.value[index].text = (symbols.value[index].text || '') + emoji;
      emitChange();
    };

    const openImagePicker = (index: number) => {
      imagePickerIndex.value = index;
      showFileManager.value = true;
    };

    const onFileChosen = (file: any) => {
      const url = file?.url || file?.fullPath || file;
      if (url && imagePickerIndex.value >= 0) {
        symbols.value[imagePickerIndex.value].image = url;
        emitChange();
      }
      showFileManager.value = false;
    };

    const clearImage = (index: number) => {
      symbols.value[index].image = '';
      emitChange();
    };

    const addSymbol = () => {
      const emojiIndex = symbols.value.length % defaultEmojis.length;
      const newId = `symbol_${Date.now()}`;

      symbols.value.push({
        id: newId,
        type: 'text',
        image: '',
        text: defaultEmojis[emojiIndex],
        label: '',
        color: '#333333',
        backgroundColor: '',
        fontSize: null,
      });
      emitChange();
    };

    const removeSymbol = (index: number) => {
      if (symbols.value.length > 2) {
        symbols.value.splice(index, 1);
        emitChange();
      }
    };

    onMounted(() => {
      parseInitialValue();
    });

    watch(() => props.parserValues.properties[0]?.value, () => {
      parseInitialValue();
    }, { deep: true });

    return {
      t,
      symbols,
      typeOptions,
      showFileManager,
      onSymbolChange,
      onEmojiInsert,
      openImagePicker,
      onFileChosen,
      clearImage,
      addSymbol,
      removeSymbol,
    };
  },
});
